import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import {
  use_slice_generation,
  use_slice_generation_price,
  use_slice_grid2d,
  use_slice_ml_asset,
  use_slice_user,
  use_slice_workspace,
} from '@/hooks/store/use_slices';
import { remote_generation_price_get_generation_price_by_hash } from '@/state/generation_price/remote_generation_price_get_generation_price_by_hash';
import { assert_array_has_1_item, assert_instanceof } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO_REQUEST__generation_price_get } from '@shared/dto/generation_price/DTO_REQUEST__generation_price_get';
import { DTO__grid2d_chunk_generation } from '@shared/dto/grid2d_chunk/DTO__grid2d_chunk_generation';

export function WATCHER__generation_price_load_generation_prices() {
  const dispatch = use_dispatch();

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { reservations_current_workspace_active_map, ml_assets_map } = use_slice_ml_asset(['reservations_current_workspace_active_map', 'ml_assets_map']);
  const { generation_current } = use_slice_generation(['generation_current']);
  const { grids2d_map } = use_slice_grid2d(['grids2d_map']);
  const { generation_prices_by_hashes_map, generation_prices_by_hash_loading_in_progress_set } = use_slice_generation_price([
    'generation_prices_by_hashes_map',
    'generation_prices_by_hash_loading_in_progress_set',
  ]);

  /*
   * EFFECTS
   */

  use_effect(() => {
    return; //todo:remove
    if (generation_current.is_stub()) return;
    if (!grids2d_map.has(generation_current._id)) return; //not loaded yet
    if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return; //no need to know price

    let grid2d = grids2d_map.get_or_assert(generation_current._id);
    let grid2d_chunk = assert_array_has_1_item(grid2d.chunks);

    assert_instanceof(grid2d_chunk, DTO__grid2d_chunk_generation);

    let requests = get_requests_to_load_price(grid2d_chunk);

    if (requests.length > 0) {
      void dispatch(remote_generation_price_get_generation_price_by_hash(requests));
    }
  }, [generation_current, grids2d_map, reservations_current_workspace_active_map, ml_assets_map, current_workspace_user]);

  /*
   * METHODS
   */

  function get_requests_to_load_price(grid2d_chunk: DTO__grid2d_chunk_generation) {
    let out_map: Map<string, DTO_REQUEST__generation_price_get> = new Map();

    for (let cell of grid2d_chunk.cells_list) {
      if (cell.image.is_generated()) continue; //already generated and settings "generate with new seeds" is disabled

      let { hash_generated_settings_clean } = cell.image;
      let { ml_asset_hash_unet } = cell.image.settings;

      if (!ml_assets_map.has(ml_asset_hash_unet)) continue; //diffusion model not loaded yet

      let diffusion_model = ml_assets_map.get_or_assert(ml_asset_hash_unet);

      let can_use_model = diffusion_model.is_free_for_rent || reservations_current_workspace_active_map.has(ml_asset_hash_unet);
      if (!can_use_model) continue; //user can't use this diffusion model

      if (generation_prices_by_hash_loading_in_progress_set.has(hash_generated_settings_clean)) continue; //already loading
      if (generation_prices_by_hashes_map.has(hash_generated_settings_clean)) continue; //already loaded
      if (out_map.has(hash_generated_settings_clean)) continue; //already added

      let request = new DTO_REQUEST__generation_price_get();
      request.hash = hash_generated_settings_clean;
      request.workspace_id = generation_current.workspace_id;
      request.generation_settings = cell.image.settings.clone();

      out_map.set(hash_generated_settings_clean, request);
    }

    return Array.from(out_map.values());
  }

  return null;
}
